import { startTransition, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { ColumnAdapter, IActionFormProps, TableAdapter } from '@makyoapp/makyo-common'
import { Button, ButtonVariant, Field, Label, SelectDropdown } from '@makyoapp/makyo-ui'

import { useForm } from '@/lib/use-form'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { SHOW_SEARCH_LIMIT } from '@/shared/constants'
import { RenderColumn } from '@/components/RenderColumn'

import { TableSelect } from '../../shared/TableSelect'

const filteredColumnTypes = [
  ColumnTypes.Slug,
  ColumnTypes.Template,
  ColumnTypes.Math,
  ColumnTypes.SingleCell,
  ColumnTypes.Function,
  ColumnTypes.Join,
  ColumnTypes.AISEO,
  ColumnTypes.Lookup,
  ColumnTypes.AITextProcessing,
  ColumnTypes.Connection,
  ColumnTypes.QRCode,
  ColumnTypes.Coordinate,
  ColumnTypes.Formula,
]

type AddToCartFormValues = {
  productTableId?: string
  productIdColumnId?: string
  cartTableId?: string
  cartProductIdColumnId?: string
  cartQuantityColumnId?: string
  cartSessionIdColumnId?: string
}

export default function ActFormAddToCart({ onSubmit, onDismiss, initialData, triggerSource }: IActionFormProps) {
  const { t } = useTranslation()
  const { tables } = useDbClientContext()

  // access form store
  const { form, onSubmit: onFormSubmit } = useForm<AddToCartFormValues>({
    defaultValues: {
      productTableId: initialData?.productTableId || undefined,
      productIdColumnId: initialData?.productIdColumnId || undefined,
      cartTableId: initialData?.cartTableId || undefined,
      cartProductIdColumnId: initialData?.cartProductIdColumnId || undefined,
      cartQuantityColumnId: initialData?.cartQuantityColumnId || undefined,
      cartSessionIdColumnId: initialData?.cartSessionIdColumnId || undefined,
    },
    validate(values, setError) {
      if (!values.productTableId || !values.productTableId?.trim().length) {
        setError('productTableId', 'Please choose a product table')
      }
      if (!values.productIdColumnId || !values.productIdColumnId?.trim().length) {
        setError('productIdColumnId', 'Please choose a product ID column')
      }
      if (!values.cartTableId || !values.cartTableId?.trim().length) {
        setError('cartTableId', 'Please choose a cart table')
      }
      if (!values.cartProductIdColumnId || !values.cartProductIdColumnId?.trim().length) {
        setError('cartProductIdColumnId', 'Please choose a product ID column for cart')
      }
      if (!values.cartQuantityColumnId || !values.cartQuantityColumnId?.trim().length) {
        setError('cartQuantityColumnId', 'Please choose a quantity column')
      }
      if (!values.cartSessionIdColumnId || !values.cartSessionIdColumnId?.trim().length) {
        setError('cartSessionIdColumnId', 'Please choose a session ID column')
      }
    },
  })

  // derived form state
  const currentProductTableId = form.getValues('productTableId')
  const currentCartTableId = form.getValues('cartTableId')
  const currentProductTable = tables.find((tb) => tb.id === currentProductTableId)
  const currentCartTable = tables.find((tb) => tb.id === currentCartTableId)

  // create table options
  const productTableOptions = useMemo(() => {
    return [{ id: '', name: 'Select Table' } as TableAdapter, ...tables.filter((tb) => !tb.isSnapshot)]
  }, [tables])

  // create cart table options (exclude selected product table)
  const cartTableOptions = useMemo(() => {
    const baseOptions = [{ id: '', name: 'Select Table' } as TableAdapter, ...tables.filter((tb) => !tb.isSnapshot)]
    if (!currentProductTableId) {
      return baseOptions
    }
    return baseOptions.filter((tb) => tb.id !== currentProductTableId)
  }, [tables, currentProductTableId])

  const productTableColumns = useMemo(() => {
    return currentProductTable?.columns
      ? currentProductTable.columns.filter((col) => !filteredColumnTypes.includes(col.type))
      : []
  }, [currentProductTable])

  const cartTableColumns = useMemo(() => {
    return currentCartTable?.columns
      ? currentCartTable.columns.filter((col) => !filteredColumnTypes.includes(col.type))
      : []
  }, [currentCartTable])

  // Filter columns for Product ID (text and number types only)
  const productIdColumns = useMemo(() => {
    const allowedTypes = [
      ColumnTypes.Text,
      ColumnTypes.Number,
      ColumnTypes.Email,
      ColumnTypes.Phone,
      ColumnTypes.Address,
      ColumnTypes.RowID,
      ColumnTypes.SessionID,
    ]
    return productTableColumns.filter((col) => allowedTypes.includes(col.type))
  }, [productTableColumns])

  // Filter cart columns for Product ID (text and number types only)
  const cartProductIdColumns = useMemo(() => {
    const allowedTypes = [
      ColumnTypes.Text,
      ColumnTypes.Number,
      ColumnTypes.Email,
      ColumnTypes.Phone,
      ColumnTypes.Address,
      ColumnTypes.RowID,
      ColumnTypes.SessionID,
    ]
    return cartTableColumns.filter((col) => allowedTypes.includes(col.type))
  }, [cartTableColumns])

  // Filter columns for Quantity (number types only)
  const quantityColumns = useMemo(() => {
    const allowedTypes = [ColumnTypes.Number]
    return cartTableColumns.filter((col) => allowedTypes.includes(col.type))
  }, [cartTableColumns])

  // Filter columns for Session ID (text and ID types)
  const sessionIdColumns = useMemo(() => {
    const allowedTypes = [
      ColumnTypes.Text,
      ColumnTypes.SessionID,
      ColumnTypes.RowID,
      ColumnTypes.Email,
      ColumnTypes.Phone,
    ]
    return cartTableColumns.filter((col) => allowedTypes.includes(col.type))
  }, [cartTableColumns])

  // change handlers
  const handleChangeProductTable = (tb: TableAdapter) => {
    startTransition(() => {
      form.setValues((prev) => {
        const newValues = {
          ...prev,
          productTableId: tb.id,
          productIdColumnId: undefined, // reset column selection when table changes
        }

        // If the new product table is the same as cart table, clear cart table too
        if (tb.id && tb.id === prev.cartTableId) {
          newValues.cartTableId = undefined
          newValues.cartProductIdColumnId = undefined
          newValues.cartQuantityColumnId = undefined
          newValues.cartSessionIdColumnId = undefined
        } else {
          // If cart table is different, still clear cart column selections
          newValues.cartProductIdColumnId = undefined
          newValues.cartQuantityColumnId = undefined
          newValues.cartSessionIdColumnId = undefined
        }

        return newValues
      })
    })
  }

  const handleChangeCartTable = (tb: TableAdapter) => {
    startTransition(() => {
      form.setValues((prev) => ({
        ...prev,
        cartTableId: tb.id,
        cartProductIdColumnId: undefined, // reset column selections when table changes
        cartQuantityColumnId: undefined,
        cartSessionIdColumnId: undefined,
      }))
    })
  }

  // save action settings
  const handleSubmit = onFormSubmit((data) => {
    onSubmit(data)
  })

  const invalidForm = !!form.errors || !currentProductTableId || !currentCartTableId

  // create column options for dropdowns
  const productColumnOptions = useMemo(() => {
    return [
      { id: '', name: 'Select column' } as Pick<ColumnAdapter, 'name' | 'id' | 'type'>,
      ...productIdColumns.map((col) => ({ id: col.id, name: col.name, type: col.type })),
    ]
  }, [productIdColumns])

  const cartProductIdColumnOptions = useMemo(() => {
    return [
      { id: '', name: 'Select column' } as Pick<ColumnAdapter, 'name' | 'id' | 'type'>,
      ...cartProductIdColumns.map((col) => ({ id: col.id, name: col.name, type: col.type })),
    ]
  }, [cartProductIdColumns])

  const cartQuantityColumnOptions = useMemo(() => {
    return [
      { id: '', name: 'Select column' } as Pick<ColumnAdapter, 'name' | 'id' | 'type'>,
      ...quantityColumns.map((col) => ({ id: col.id, name: col.name, type: col.type })),
    ]
  }, [quantityColumns])

  const cartSessionIdColumnOptions = useMemo(() => {
    return [
      { id: '', name: 'Select column' } as Pick<ColumnAdapter, 'name' | 'id' | 'type'>,
      ...sessionIdColumns.map((col) => ({ id: col.id, name: col.name, type: col.type })),
    ]
  }, [sessionIdColumns])

  return (
    <div className='max-w-[768px]'>
      <div className='p-4 max-h-[75vh] overflow-auto'>
        {/* Description */}
        <div className='mb-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg'>
          <p className='text-sm text-blue-200 leading-relaxed'>
            💡 Adds a product to the cart. If the product is already in the cart for the current session, it will
            increment the existing quantity. Otherwise, it creates a new cart item with the specified quantity.
          </p>
        </div>

        {/* Product Table Selection */}
        <Field className='min-h-0'>
          <Label required>Product Table</Label>
          <TableSelect
            options={productTableOptions}
            value={productTableOptions.find((tb) => tb.id === form.getValues('productTableId'))}
            onChange={handleChangeProductTable}
            error={form.errors?.productTableId}
          />
        </Field>

        {/* Product ID Column Selection */}
        <Field className='min-h-0'>
          <Label required>Product ID</Label>
          <SelectDropdown
            options={productColumnOptions}
            value={productColumnOptions.find((col) => col.id === form.getValues('productIdColumnId'))}
            onChange={(col) => {
              form.setValues('productIdColumnId', col.id)
            }}
            optionLabel={(col) => `${col.name}`}
            renderOption={(col) => <RenderColumn withStyle={false} col={col} />}
            withSearch={productColumnOptions.length > SHOW_SEARCH_LIMIT}
            menuContainerClassName='mk-ag-sdd'
            disabled={!currentProductTableId}
            portalMenu
            menuFullWidth={false}
            menuZIndex='z-302'
          />
        </Field>

        <hr className='my-4 bdr' />

        {/* Cart Table Selection */}
        <Field className='min-h-0'>
          <Label required>Cart Table</Label>
          <TableSelect
            options={cartTableOptions}
            value={cartTableOptions.find((tb) => tb.id === form.getValues('cartTableId'))}
            onChange={handleChangeCartTable}
            error={form.errors?.cartTableId || (!currentProductTableId ? 'Select a product table first' : null)}
            disabled={!currentProductTableId}
          />
        </Field>

        {/* Cart Product ID Column Selection */}
        <Field className='min-h-0'>
          <Label required>Product ID</Label>
          <SelectDropdown
            options={cartProductIdColumnOptions}
            value={cartProductIdColumnOptions.find((col) => col.id === form.getValues('cartProductIdColumnId'))}
            onChange={(col) => {
              form.setValues('cartProductIdColumnId', col.id)
            }}
            optionLabel={(col) => `${col.name}`}
            renderOption={(col) => <RenderColumn withStyle={false} col={col} />}
            withSearch={cartProductIdColumnOptions.length > SHOW_SEARCH_LIMIT}
            menuContainerClassName='mk-ag-sdd'
            disabled={!currentCartTableId}
            portalMenu
            menuFullWidth={false}
            menuZIndex='z-302'
          />
        </Field>

        {/* Cart Quantity Column Selection */}
        <Field className='min-h-0'>
          <Label required>Quantity</Label>
          <SelectDropdown
            options={cartQuantityColumnOptions}
            value={cartQuantityColumnOptions.find((col) => col.id === form.getValues('cartQuantityColumnId'))}
            onChange={(col) => {
              form.setValues('cartQuantityColumnId', col.id)
            }}
            optionLabel={(col) => `${col.name}`}
            renderOption={(col) => <RenderColumn withStyle={false} col={col} />}
            withSearch={cartQuantityColumnOptions.length > SHOW_SEARCH_LIMIT}
            menuContainerClassName='mk-ag-sdd'
            disabled={!currentCartTableId}
            portalMenu
            menuFullWidth={false}
            menuZIndex='z-302'
          />
        </Field>

        {/* Cart Session ID Column Selection */}
        <Field className='min-h-0'>
          <Label required>Session ID</Label>
          <SelectDropdown
            options={cartSessionIdColumnOptions}
            value={cartSessionIdColumnOptions.find((col) => col.id === form.getValues('cartSessionIdColumnId'))}
            onChange={(col) => {
              form.setValues('cartSessionIdColumnId', col.id)
            }}
            optionLabel={(col) => `${col.name}`}
            renderOption={(col) => <RenderColumn withStyle={false} col={col} />}
            withSearch={cartSessionIdColumnOptions.length > SHOW_SEARCH_LIMIT}
            menuContainerClassName='mk-ag-sdd'
            disabled={!currentCartTableId}
            portalMenu
            menuFullWidth={false}
            menuZIndex='z-302'
          />
        </Field>
      </div>

      <div className='px-4 py-2 border-t bdr flex justify-between items-center'>
        <Button variant={ButtonVariant.Outlined} onClick={onDismiss}>
          <Trans>Cancel</Trans>
        </Button>
        <Button onClick={handleSubmit} disabled={invalidForm}>
          <Trans>Save</Trans>
        </Button>
      </div>
    </div>
  )
}
